import { CSSProperties, memo } from 'react';

import type { PipelineColumn as PipelineColumnDto } from '@/pages/pipelines/common/types';

import { PipelineCompactColumn } from './compact-column';
import { PipelineExpandedColumn } from './expanded-column';
import { getPipelineStageChrome } from './stage-presentation';

type Props = {
  column: PipelineColumnDto;
  onSelectCard: (_cardId: string) => void;
  expanded?: boolean;
  dragAvailable?: boolean;
  dragInteractionDisabled?: boolean;
  stageFiltered?: boolean;
  sortActive?: boolean;
  dragSourceStage?: string | null;
  onExpand?: (_stage: string) => void;
  onToggleStageFilter?: (_stage: string) => void;
  onToggleSort?: () => void;
  onAddCard?: (_stage: string) => void;
};

export const PipelineColumn = memo(
  ({
    column,
    expanded = true,
    dragAvailable = false,
    dragInteractionDisabled = false,
    stageFiltered = false,
    sortActive = false,
    dragSourceStage,
    onExpand,
    onSelectCard,
    onToggleStageFilter,
    onToggleSort,
    onAddCard,
  }: Props) => {
    const stageChrome = getPipelineStageChrome(column);
    const stageColor = stageChrome.color;
    const stageStyle = {
      '--stage-color': stageColor,
      '--stage-compact-surface': stageChrome.compactSurface,
      '--stage-compact-border': stageChrome.compactBorder,
      '--stage-compact-badge-surface': stageChrome.compactBadgeSurface,
    } as CSSProperties;

    if (!expanded) {
      return (
        <PipelineCompactColumn
          column={column}
          stageStyle={stageStyle}
          dragAvailable={dragAvailable}
          dragInteractionDisabled={dragInteractionDisabled}
          dragSourceStage={dragSourceStage}
          onExpand={onExpand}
        />
      );
    }

    return (
      <PipelineExpandedColumn
        column={column}
        stageColor={stageColor}
        stageStyle={stageStyle}
        dragAvailable={dragAvailable}
        dragInteractionDisabled={dragInteractionDisabled}
        stageFiltered={stageFiltered}
        sortActive={sortActive}
        dragSourceStage={dragSourceStage}
        onSelectCard={onSelectCard}
        onToggleStageFilter={onToggleStageFilter}
        onToggleSort={onToggleSort}
        onAddCard={onAddCard}
      />
    );
  },
);

PipelineColumn.displayName = 'PipelineColumn';
